import Events from 'lib/events';
import ThisBound from 'lib/object/this-bound';
import {get_element_box, get_styles, set_styles, StyleObject} from 'lib/utilities/dom';
import {function_delay} from 'lib/utilities/time';
import {abs_diff} from 'lib/utilities/misc';

/**
 * This class solves the problem of long links and email addresses, by converging on the maximum font-size that will
 * fit the text inside the element.
 */
export default class AutoFontSize extends ThisBound {
    private elements = this.findElements();

    private events = new Events;

    private originalStyle = new Map<HTMLElement, StyleObject>();

    private resizeDelayed = function_delay(250, this.resize);

    constructor(private selector: string = 'p a') {
        super();

        this.addEvents();
    }

    private findElements() {
        return document.querySelectorAll<HTMLElement>(this.selector);
    }

    private addEvents() {
        this.events.on(window, 'resize', this.triggerResize);
    }

    private triggerResize() {
        this.resize();
        this.resizeDelayed();
    }

    destroy() {
        this.events.offAll();
    }

    resize() {
        for (const element of this.elements) {
            this.resizeElement(element);
        }
    }

    private resetElement(element: HTMLElement) {
        if (this.originalStyle.has(element)) {
            set_styles(element, this.originalStyle.get(element));
        }
    }

    private saveOriginalStyle(element: HTMLElement) {
        this.originalStyle.set(element, get_styles(element, ['fontSize']));
    }

    private resizeElement(element: HTMLElement) {
        this.resetElement(element);
        this.saveOriginalStyle(element);

        const parentBox = get_element_box(element.parentElement);
        const parentMaxWidth = parentBox.rect.width - parentBox.paddingX;

        let style = getComputedStyle(element);
        let fontSize = parseFloat(style.fontSize);
        let width = element.getBoundingClientRect().width;
        let limit = 5;
        let diff = 1;

        // 1. Only adjust font-size if it is too big
        // 2. Require min diff to prevent it edging infinitely closer to the required size
        // 3. Allow a maximum of 5 iterations
        while (width > parentMaxWidth && diff > 0.01 && limit >= 0) {
            --limit;

            // Approximate the font-size needed to fit the text in one line
            const newFontSize = fontSize * parentMaxWidth / width;

            diff = abs_diff(newFontSize, fontSize);
            fontSize = newFontSize;
            element.style.setProperty('font-size', fontSize + 'px', 'important');
            width = element.getBoundingClientRect().width;
        }
    }
}